<!-- Базовая кнопка - Atom -->
<template>
  <button :type="type" :disabled="disabled || loading" :aria-label="ariaLabel"
    class="inline-flex items-center justify-center rotate-45font-normal border focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200"
    :class="[
      buttonClasses,
      sizeClasses,
      (showArrow || $slots.arrow) && 'gap-[6px]',
      disabled || loading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
    ]" @click="handleClick">
    <slot />

    <!-- Кастомная стрелка через slot (если передана) -->
    <slot name="arrow">
      <!-- Дефолтная CSS стрелка для кнопок с действием (из Figma) -->
      <div v-if="showArrow" class="flex items-center justify-center w-[7.485px] h-[7.485px] transform ">
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 11 10" fill="none">
          <path
            d="M4.63393 0.772284L7.94942 4.08778H0.000109958L0.000109832 5.17982L7.94942 5.17982L4.63393 8.49531L5.40623 9.26762L10.04 4.6338L5.40623 -1.91303e-05L4.63393 0.772284Z"
            fill="currentColor" />
        </svg>
      </div>
    </slot>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ButtonProps } from '@/lib/types'

interface Props extends ButtonProps {
  showArrow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'primary',
  size: 'md',
  type: 'button',
  disabled: false,
  loading: false,
  showArrow: false
})

const emit = defineEmits<{
  click: []
}>()

// Классы для разных тонов кнопок (из дизайна Figma)
const buttonClasses = computed(() => {
  switch (props.tone) {
    case 'primary':
      return 'btn-primary'
    case 'secondary':
      return 'btn-secondary'
    case 'outline':
      return 'btn-outline'
    case 'gradient':
      return 'btn-gradient'
    case 'light-blue':
      return 'btn-light-blue'
    case 'light-red':
      return 'btn-light-red'
    case 'white':
      return 'btn-white'
    case 'black':
      return 'btn-black'
    default:
      return 'btn-primary'
  }
})

// Классы для размеров
const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'px-3 py-1.5 text-sm rounded-lg'
    case 'lg':
      return 'px-6 py-3 text-lg rounded-xl'
    case 'figma':
      return 'px-[20px] py-[15px] text-[15px] leading-none rounded-[15px] h-[45px] gap-[6px] whitespace-nowrap'
    default:
      return 'px-4 py-2 text-base rounded-lg'
  }
})

const handleClick = () => {
  if (!props.disabled && !props.loading) {
    emit('click')
  }
}
</script>

<style scoped>
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* CSS стрелка */
.arrow-shape {
  width: 0;
  height: 0;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
  border-bottom: 4px solid currentColor;
  transform: rotate(-45deg);
}

/* Стили кнопок */
.btn-primary {
  background: linear-gradient(90deg, #ff3f1a 4.2%, #ff6d51 53.14%, #ff3f1a 100.23%);
  color: white;
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: inset 0px -5px 13.4px 0px rgba(255, 63, 26, 0.22);
}

.btn-secondary {
  background-color: var(--color-surface);
  color: var(--color-primary);
  border-color: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(25px);
}

.btn-outline {
  background: rgba(255, 63, 26, 0.01);
  color: white;
  border-color: rgba(255, 170, 154, 0.2);
  box-shadow: inset 0px -5px 13.4px 0px rgba(255, 63, 26, 0.22);
}

.btn-gradient {
  background: linear-gradient(90deg, #FF3F1A 0%, #FF6D51 51%, #FF3F1A 100%), linear-gradient(0deg, #FFFFFF 0%, #FFFFFF 100%);
  color: white;
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0px 0px 20px 0px rgba(255, 63, 26, 0.51);
}

.btn-light-blue {
  background: linear-gradient(90deg, #cefaff 4.2%, #ebfdff 53.14%, #cefaff 100.23%);
  color: black;
  border-color: rgba(255, 255, 255, 0.25);
}

.btn-light-red {
  background: linear-gradient(90deg, #ff3f1a 4.2%, #ff6d51 53.14%, #ff3f1a 100.23%);
  color: white;
  border-color: rgba(255, 255, 255, 0.25);
}

.btn-white {
  background: linear-gradient(90deg, #DBDBDB 4.2%, #FFFFFF 53.14%, #DBDBDB 100.23%);
  color: black;
  border-color: rgba(255, 255, 255, 0.58);
}

.btn-black {
  background: linear-gradient(90deg, #000000 4.2%, #181818 53.14%, #000000 100.23%);
  color: white;
  border-color: rgba(255, 255, 255, 0.25);
}
</style>
